import { useMemo, useState } from 'react';
import { api as http } from '../lib/api.ts';
import { item, separator, label as menuLabel, useMenu } from './menu.tsx';
import {
  addRow, deleteRow, openPage, patchLocalDatabase, refreshDatabase, setRowProps, setRowTitle,
  useStore,
} from './store.ts';
import type { Database, DbProp, DbView, Page, PropType, ViewKind } from './types.ts';

const PROP_TYPES: { type: PropType; label: string }[] = [
  { type: 'text', label: 'Text' },
  { type: 'number', label: 'Number' },
  { type: 'select', label: 'Select' },
  { type: 'multi_select', label: 'Multi-select' },
  { type: 'date', label: 'Date' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'url', label: 'URL' },
  { type: 'email', label: 'Email' },
  { type: 'phone', label: 'Phone' },
];

const VIEW_KINDS: { kind: ViewKind; label: string; icon: string }[] = [
  { kind: 'table', label: 'Table', icon: '▦' },
  { kind: 'board', label: 'Board', icon: '▥' },
  { kind: 'list', label: 'List', icon: '☰' },
  { kind: 'gallery', label: 'Gallery', icon: '▢' },
];

export function DatabaseView({ dbId, titleless }: { dbId?: string; titleless?: boolean }) {
  const db = useStore((s) => (dbId ? s.doc?.databases[dbId] : undefined));
  const page = useStore((s) => (dbId ? s.pages[dbId] ?? (s.doc?.page.id === dbId ? s.doc.page : undefined) : undefined));
  const [viewId, setViewId] = useState<string | null>(null);
  const [menu, openMenu] = useMenu();

  const view = useMemo(() => {
    if (!db?.views.length) return null;
    return db.views.find((v) => v.id === viewId) ?? db.views[0]!;
  }, [db, viewId]);

  if (!dbId) return <div className="dbempty">This block points at a database that is gone.</div>;
  if (!db || !view) return <div className="dbempty">Loading…</div>;

  const titleName = (page?.props?.title_name as string | undefined) || 'Name';

  const changeView = async (patch: Partial<DbView>) => {
    patchLocalDatabase(dbId, { views: db.views.map((v) => (v.id === view.id ? { ...v, ...patch } : v)) });
    await http.patch(`/api/db/views/${view.id}`, patch).catch(() => refreshDatabase(dbId));
  };

  const addView = async (kind: ViewKind) => {
    const fresh = await http.post<DbView>(`/api/db/${dbId}/views`, {
      kind, name: VIEW_KINDS.find((v) => v.kind === kind)?.label ?? 'View',
    });
    patchLocalDatabase(dbId, { views: [...db.views, fresh] });
    setViewId(fresh.id);
  };

  const addProp = async (type: PropType) => {
    const name = `${PROP_TYPES.find((p) => p.type === type)?.label ?? 'Property'} ${db.props.length + 1}`;
    const fresh = await http.post<DbProp>(`/api/db/${dbId}/props`, { name, type });
    patchLocalDatabase(dbId, { props: [...db.props, fresh] });
  };

  return (
    <div className="db" contentEditable={false}>
      <div className="db-bar">
        <div className="db-views">
          {db.views.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`db-tab${v.id === view.id ? ' on' : ''}`}
              onClick={() => setViewId(v.id)}
              onDoubleClick={(e) => openMenu(e, [
                menuLabel('View'),
                ...VIEW_KINDS.map((k) => item(k.label, () => void changeView({ kind: k.kind }))),
                separator(),
                item('Delete view', () => {
                  void http.del(`/api/db/views/${v.id}`)
                    .then(() => patchLocalDatabase(dbId, { views: db.views.filter((x) => x.id !== v.id) }))
                    .catch(() => refreshDatabase(dbId));
                }, { danger: true }),
              ])}
            >
              <span aria-hidden>{VIEW_KINDS.find((k) => k.kind === v.kind)?.icon ?? '▦'}</span> {v.name}
            </button>
          ))}
          <button
            type="button" className="db-tab add" aria-label="Add a view"
            onClick={(e) => openMenu(e, VIEW_KINDS.map((k) => item(k.label, () => void addView(k.kind))))}
          >+</button>
        </div>

        <div className="db-tools">
          {view.kind === 'board' ? (
            <button
              type="button" className="db-tool"
              onClick={(e) => openMenu(e, [
                menuLabel('Group by'),
                ...db.props.filter((p) => p.type === 'select').map((p) =>
                  item(p.name, () => void changeView({ group_by: p.id }))),
              ])}
            >Group</button>
          ) : null}
          <button
            type="button" className="db-tool"
            onClick={(e) => openMenu(e, [
              menuLabel('Sort by'),
              item(titleName, () => void changeView({ sorts: [{ prop: 'title', dir: 'asc' }] })),
              ...db.props.map((p) => item(p.name, () => void changeView({ sorts: [{ prop: p.id, dir: 'asc' }] }))),
              separator(),
              item('Clear sorting', () => void changeView({ sorts: [] })),
            ])}
          >Sort</button>
          <button type="button" className="db-new" onClick={() => void addRow(dbId)}>New</button>
        </div>
      </div>

      {view.kind === 'table' ? (
        <TableView db={db} dbId={dbId} view={view} titleName={titleName} onAddProp={addProp} />
      ) : view.kind === 'board' ? (
        <BoardView db={db} dbId={dbId} view={view} />
      ) : view.kind === 'gallery' ? (
        <GalleryView db={db} dbId={dbId} view={view} />
      ) : (
        <ListView db={db} dbId={dbId} view={view} />
      )}
      {menu}
    </div>
  );
}

function sortRows(rows: Page[], view: DbView): Page[] {
  const sorts = view.sorts ?? [];
  if (!sorts.length) return [...rows].sort((a, b) => a.position - b.position);
  return [...rows].sort((a, b) => {
    for (const { prop, dir } of sorts) {
      const av = String(prop === 'title' ? a.title : a.props[prop] ?? '');
      const bv = String(prop === 'title' ? b.title : b.props[prop] ?? '');
      const cmp = av.localeCompare(bv, undefined, { numeric: true });
      if (cmp) return dir === 'desc' ? -cmp : cmp;
    }
    return a.position - b.position;
  });
}

function TableView({
  db, dbId, view, titleName, onAddProp,
}: {
  db: Database; dbId: string; view: DbView; titleName: string;
  onAddProp: (type: PropType) => Promise<void>;
}) {
  const [menu, openMenu] = useMenu();
  const visible = db.props.filter((p) => !view.hidden?.includes(p.id));
  const rows = sortRows(db.rows, view);

  const propMenu = (prop: DbProp) => [
    menuLabel('Property type'),
    ...PROP_TYPES.map((t) => item(t.label + (t.type === prop.type ? '  ✓' : ''), () => {
      void http.patch(`/api/db/props/${prop.id}`, { type: t.type }).then(() => refreshDatabase(dbId));
    })),
    separator(),
    item('Rename', () => {
      const name = window.prompt('Property name', prop.name);
      if (name) void http.patch(`/api/db/props/${prop.id}`, { name }).then(() => refreshDatabase(dbId));
    }),
    item('Hide in this view', () => {
      void http.patch(`/api/db/views/${view.id}`, { hidden: [...(view.hidden ?? []), prop.id] })
        .then(() => refreshDatabase(dbId));
    }),
    item('Delete property', () => {
      void http.del(`/api/db/props/${prop.id}`).then(() => refreshDatabase(dbId));
    }, { danger: true }),
  ];

  return (
    <div className="db-table-wrap">
      <table className="db-table">
        <thead>
          <tr>
            <th className="db-title-col">{titleName}</th>
            {visible.map((prop) => (
              <th key={prop.id}>
                <button type="button" className="db-head" onClick={(e) => openMenu(e, propMenu(prop))}>
                  {prop.name}
                </button>
              </th>
            ))}
            <th className="db-add-col">
              <button
                type="button" aria-label="Add a property"
                onClick={(e) => openMenu(e, PROP_TYPES.map((t) => item(t.label, () => void onAddProp(t.type))))}
              >+</button>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="db-title-col">
                <div className="db-title-cell">
                  <input
                    value={row.title}
                    placeholder="Untitled"
                    onChange={(e) => void setRowTitle(dbId, row.id, e.target.value)}
                    aria-label={titleName}
                  />
                  <button type="button" className="db-open" onClick={() => void openPage(row.id)}>Open</button>
                  <button
                    type="button" className="db-rowmenu" aria-label="Row options"
                    onClick={(e) => openMenu(e, [
                      item('Open', () => void openPage(row.id)),
                      item('Delete', () => void deleteRow(dbId, row.id), { danger: true }),
                    ])}
                  >⋯</button>
                </div>
              </td>
              {visible.map((prop) => (
                <td key={prop.id}>
                  <Cell dbId={dbId} row={row} prop={prop} />
                </td>
              ))}
              <td />
            </tr>
          ))}
          <tr className="db-newrow">
            <td colSpan={visible.length + 2}>
              <button type="button" onClick={() => void addRow(dbId)}>+ New</button>
            </td>
          </tr>
        </tbody>
      </table>
      {menu}
    </div>
  );
}

function BoardView({ db, dbId, view }: { db: Database; dbId: string; view: DbView }) {
  const groupProp = db.props.find((p) => p.id === view.group_by)
    ?? db.props.find((p) => p.type === 'select');
  if (!groupProp) {
    return <div className="dbempty">Add a select property, then group the board by it.</div>;
  }
  const columns = [...groupProp.options, { id: '', name: 'No ' + groupProp.name.toLowerCase(), color: 'gray' }];
  const rows = sortRows(db.rows, view);

  const move = (rowId: string, optionId: string) => {
    void setRowProps(dbId, rowId, { [groupProp.id]: optionId || null });
  };

  return (
    <div className="db-board">
      {columns.map((col) => {
        const cards = rows.filter((r) => String(r.props[groupProp.id] ?? '') === col.id);
        return (
          <div
            key={col.id || 'none'}
            className="db-col"
            onDragOver={(e) => { if (e.dataTransfer.types.includes('text/vnote-row')) e.preventDefault(); }}
            onDrop={(e) => {
              const rowId = e.dataTransfer.getData('text/vnote-row');
              if (rowId) { e.preventDefault(); move(rowId, col.id); }
            }}
          >
            <div className="db-col-head">
              <span className={`chip tint-${col.color}`}>{col.name}</span>
              <span className="db-col-count">{cards.length}</span>
            </div>
            {cards.map((row) => (
              <div
                key={row.id}
                className="db-card"
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/vnote-row', row.id)}
                onClick={() => void openPage(row.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') void openPage(row.id); }}
              >
                <div className="db-card-title">{row.title || 'Untitled'}</div>
                <div className="db-card-props">
                  {db.props.filter((p) => p.id !== groupProp.id && row.props[p.id]).slice(0, 3).map((p) => (
                    <span key={p.id} className="db-card-prop">{formatValue(p, row.props[p.id])}</span>
                  ))}
                </div>
              </div>
            ))}
            <button type="button" className="db-col-add" onClick={() => void addRow(dbId, { [groupProp.id]: col.id || null })}>
              + New
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ListView({ db, dbId, view }: { db: Database; dbId: string; view: DbView }) {
  const rows = sortRows(db.rows, view);
  return (
    <div className="db-list">
      {rows.map((row) => (
        <button key={row.id} type="button" className="db-list-row" onClick={() => void openPage(row.id)}>
          <span className="db-list-icon">{row.icon || '📄'}</span>
          <span className="db-list-title">{row.title || 'Untitled'}</span>
          <span className="db-list-props">
            {db.props.filter((p) => row.props[p.id]).slice(0, 2).map((p) => (
              <span key={p.id}>{formatValue(p, row.props[p.id])}</span>
            ))}
          </span>
        </button>
      ))}
      <button type="button" className="db-list-new" onClick={() => void addRow(dbId)}>+ New</button>
    </div>
  );
}

function GalleryView({ db, dbId, view }: { db: Database; dbId: string; view: DbView }) {
  const rows = sortRows(db.rows, view);
  return (
    <div className="db-gallery">
      {rows.map((row) => (
        <button key={row.id} type="button" className="db-tile" onClick={() => void openPage(row.id)}>
          <span className="db-tile-icon">{row.icon || '📄'}</span>
          <span className="db-tile-title">{row.title || 'Untitled'}</span>
          <span className="db-tile-props">
            {db.props.filter((p) => row.props[p.id]).slice(0, 3).map((p) => (
              <span key={p.id}>{formatValue(p, row.props[p.id])}</span>
            ))}
          </span>
        </button>
      ))}
      <button type="button" className="db-tile new" onClick={() => void addRow(dbId)}>+ New</button>
    </div>
  );
}

// ---- one cell -------------------------------------------------------------

function Cell({ dbId, row, prop }: { dbId: string; row: Page; prop: DbProp }) {
  const [menu, openMenu] = useMenu();
  const value = row.props[prop.id];
  const write = (next: unknown) => void setRowProps(dbId, row.id, { [prop.id]: next });

  if (prop.type === 'checkbox') {
    return (
      <input
        type="checkbox" className="db-check" checked={!!value}
        onChange={(e) => write(e.target.checked)} aria-label={prop.name}
      />
    );
  }

  if (prop.type === 'select' || prop.type === 'multi_select') {
    const multi = prop.type === 'multi_select';
    const chosen: string[] = multi
      ? (Array.isArray(value) ? value.map(String) : value ? [String(value)] : [])
      : (value ? [String(value)] : []);

    const pick = (optionId: string) => {
      if (!multi) { write(optionId || null); return; }
      write(chosen.includes(optionId) ? chosen.filter((c) => c !== optionId) : [...chosen, optionId]);
    };

    const addOption = async () => {
      const name = window.prompt(`New option for ${prop.name}`);
      if (!name) return;
      const id = name.toLowerCase().replace(/\s+/g, '-').slice(0, 40) || `opt${prop.options.length + 1}`;
      const options = [...prop.options, { id, name, color: COLORS[prop.options.length % COLORS.length]! }];
      await http.patch(`/api/db/props/${prop.id}`, { options });
      await refreshDatabase(dbId);
      pick(id);
    };

    return (
      <>
        <button
          type="button" className="db-cell db-select"
          onClick={(e) => openMenu(e, [
            ...(multi ? [] : [item('— none —', () => pick(''))]),
            ...prop.options.map((o) =>
              item(`${o.name}${chosen.includes(o.id) ? '  ✓' : ''}`, () => pick(o.id))),
            separator(),
            item('New option…', () => void addOption()),
          ])}
        >
          {chosen.length
            ? chosen.map((id) => {
                const option = prop.options.find((o) => o.id === id);
                return <span key={id} className={`chip tint-${option?.color ?? 'gray'}`}>{option?.name ?? id}</span>;
              })
            : <span className="db-blank">Empty</span>}
        </button>
        {menu}
      </>
    );
  }

  const type = prop.type === 'number' ? 'number'
    : prop.type === 'date' ? 'date'
    : prop.type === 'email' ? 'email'
    : prop.type === 'url' ? 'url'
    : prop.type === 'phone' ? 'tel'
    : 'text';

  return (
    <input
      className="db-cell"
      type={type}
      value={value == null ? '' : String(value)}
      placeholder="Empty"
      aria-label={prop.name}
      onChange={(e) => write(e.target.value)}
    />
  );
}

const COLORS = ['gray', 'brown', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'red'];

function formatValue(prop: DbProp, value: unknown): string {
  if (value == null || value === '') return '';
  if (prop.type === 'select') return prop.options.find((o) => o.id === String(value))?.name ?? String(value);
  if (prop.type === 'multi_select') {
    const ids = Array.isArray(value) ? value.map(String) : [String(value)];
    return ids.map((id) => prop.options.find((o) => o.id === id)?.name ?? id).join(', ');
  }
  if (prop.type === 'checkbox') return value ? '✓' : '';
  return String(value);
}
